/**
 * EmptyState Component
 *
 * Displays a message when there's no data to show
 */

import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { InboxOutlined } from '@mui/icons-material';

export interface EmptyStateProps {
    icon?: React.ReactNode;
    title: string;
    description?: string;
    actionLabel?: string;
    onAction?: () => void;
    isDarkMode: boolean;
    compact?: boolean;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
    icon,
    title,
    description,
    actionLabel,
    onAction,
    isDarkMode,
    compact = false,
}) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: compact ? 3 : 6,
                px: 3,
                textAlign: 'center',
            }}
        >
            <Box
                sx={{
                    width: compact ? 48 : 64,
                    height: compact ? 48 : 64,
                    borderRadius: '50%',
                    bgcolor: isDarkMode ? '#334155' : '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                }}
            >
                {icon || (
                    <InboxOutlined
                        sx={{
                            fontSize: compact ? 24 : 32,
                            color: isDarkMode ? '#64748b' : '#94a3b8',
                        }}
                    />
                )}
            </Box>
            <Typography
                variant={compact ? 'body1' : 'h6'}
                sx={{
                    fontWeight: 600,
                    color: isDarkMode ? '#f1f5f9' : '#1e293b',
                    mb: description ? 1 : 0,
                }}
            >
                {title}
            </Typography>
            {description && (
                <Typography
                    variant="body2"
                    sx={{
                        color: isDarkMode ? '#94a3b8' : '#64748b',
                        maxWidth: 300,
                        mb: actionLabel ? 2 : 0,
                    }}
                >
                    {description}
                </Typography>
            )}
            {actionLabel && onAction && (
                <Button
                    variant="contained"
                    size={compact ? 'small' : 'medium'}
                    onClick={onAction}
                    sx={{
                        bgcolor: '#6366f1',
                        '&:hover': {
                            bgcolor: '#4f46e5',
                        },
                    }}
                >
                    {actionLabel}
                </Button>
            )}
        </Box>
    );
};

export default EmptyState;
